/**
 * profile — what a visitor tends to like, as the wall reads it (plan `2026_10_29_wall_keywords_profile.md`, Phase B).
 * A visitor's touches (the tiles they armed, tapped a chip on, generated from) are tile ids, most recent first, kept
 * in the visitor's session (`touches.server.ts`). The profile weighs the facet tokens of those tiles, a touch
 * counting less the older it is and a token most packs share counting little, and keeps the two strongest as the **profile key**: a short canonical string
 * (`use:wedding,colour:red`) that rides in the `p` query parameter, so the `/wall` edge cache stays keyed by a small
 * space. Pure: no I/O, so the client, the loaders and the tests read the same functions.
 */

/** Touches kept, most recent first: the profile follows what a visitor does now. */
export const MAX_TOUCHES = 24
/** Tokens a visitor may hold dropped from their lean at once; the oldest drop is forgotten past it. */
export const MAX_SUPPRESSED = 12
/** Tokens in a key. */
export const PROFILE_TOKENS = 2
/** A touch counts this much of the one after it. */
const RECENCY_DECAY = 0.8

/** A facet token, `key:value` (`facets.ts` `facetToken`). A key starts with a letter, so a typed `10:30` is no token. */
export const TOKEN_PATTERN = /^[a-z][a-z0-9-]{0,39}:[a-z0-9-]{1,40}$/

/** `id` first, the rest after it without `id`, cut to MAX_TOUCHES. */
export function pushTouch(touches: readonly number[], id: number): number[] {
    return [id, ...touches.filter((t) => t !== id)].slice(0, MAX_TOUCHES)
}

/**
 * A signed-in visitor's touches: the session's (this device, what they just did) first, then the account's own
 * signals (`accountTouches`, most recent first), distinct, cut to MAX_TOUCHES. The session's have no clock, so they
 * lead; a tile both hold counts once, at its session place.
 */
export function mergeTouches(session: readonly number[], account: readonly number[]): number[] {
    return [...new Set([...session, ...account])].slice(0, MAX_TOUCHES)
}

/** What a session holds under `wallTouches`, or `[]` when it holds anything else. */
export function readTouches(raw: unknown): number[] {
    if (!Array.isArray(raw)) return []
    const ids = raw.filter((v): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v > 0)
    return [...new Set(ids)].slice(0, MAX_TOUCHES)
}

/** What a session holds under `wallSuppressed` (the lean tokens the visitor dropped), or `[]` when it holds anything else. */
export function readSuppressed(raw: unknown): string[] {
    if (!Array.isArray(raw)) return []
    const tokens = raw.filter((v): v is string => typeof v === 'string' && TOKEN_PATTERN.test(v))
    return [...new Set(tokens)].slice(0, MAX_SUPPRESSED)
}

/** `token` first, the rest after it without `token`, cut to MAX_SUPPRESSED. */
export function pushSuppressed(suppressed: readonly string[], token: string): string[] {
    return [token, ...suppressed.filter((t) => t !== token)].slice(0, MAX_SUPPRESSED)
}

/**
 * How much a token says about a visitor: the log of the inverse share of the house's packs that carry it (an IDF,
 * `facetShares.json`). A token most packs share (`era:contemporary`, two thirds of Narcis) says little and would
 * otherwise win every key by being everywhere; a token the shares do not know counts 1.
 */
export function rarity(share: number | undefined): number {
    return share === undefined || share <= 0 ? 1 : Math.log(1 / share)
}

/**
 * What each token of `touches` (most recent first) weighs for the visitor: the sum of its touches' recency weights
 * (0.8^i) times its `rarity` (`shareOf`, the share of the house's packs that carry it). `tokensOf` gives a tile's facet
 * tokens; a tile it does not know counts for nothing. The one computation under the profile key and under the panel's
 * ranking of facet values (`facetOffer.ts`).
 */
export function tokenWeights(
    touches: readonly number[],
    tokensOf: (id: number) => readonly string[] | undefined,
    shareOf: (token: string) => number | undefined = () => undefined,
): Map<string, number> {
    const weight = new Map<string, number>()
    touches.forEach((id, i) => {
        const w = RECENCY_DECAY ** i
        for (const token of tokensOf(id) ?? []) weight.set(token, (weight.get(token) ?? 0) + w * rarity(shareOf(token)))
    })
    return weight
}

/**
 * The profile key of `touches` (most recent first): its two strongest `tokenWeights`; ties go to the token of the
 * facet the house lists first (`facetOrder`: the house's facet keys, its main axis first), then to the token's own
 * text. A `suppressed` token (one the visitor dropped from their lean) never enters the key: the next strongest takes
 * its slot. `''` when no touch carries a token: no profile.
 */
export function profileKey(
    touches: readonly number[],
    tokensOf: (id: number) => readonly string[] | undefined,
    facetOrder: readonly string[],
    shareOf: (token: string) => number | undefined = () => undefined,
    suppressed: readonly string[] = [],
): string {
    const weight = tokenWeights(touches, tokensOf, shareOf)
    const order = (token: string) => {
        const at = facetOrder.indexOf(token.slice(0, token.indexOf(':')))
        return at === -1 ? facetOrder.length : at
    }
    const strongest = [...weight.entries()]
        .filter(([token, w]) => TOKEN_PATTERN.test(token) && w > 0 && !suppressed.includes(token))
        .sort((a, b) => b[1] - a[1] || order(a[0]) - order(b[0]) || (a[0] < b[0] ? -1 : 1))
        .slice(0, PROFILE_TOKENS)
        .map(([token]) => token)
    return strongest.sort().join(',')
}

/**
 * The lean a browser holds, carried to another session row's data: what `from` holds of `wallTouches` and
 * `wallSuppressed` when `to` holds none of either, else `null` (a browser that has a lean of its own keeps it).
 */
export function carriedLean(from: unknown, to: unknown): {wallTouches: number[]; wallSuppressed: string[]} | null {
    const rowOf = (data: unknown) => (typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {})
    const touches = readTouches(rowOf(from).wallTouches)
    const suppressed = readSuppressed(rowOf(from).wallSuppressed)
    if (touches.length === 0 && suppressed.length === 0) return null
    if (readTouches(rowOf(to).wallTouches).length > 0 || readSuppressed(rowOf(to).wallSuppressed).length > 0) return null
    return {wallTouches: touches, wallSuppressed: suppressed}
}

/** The tokens of a `p` parameter: canonical (sorted, distinct, at most PROFILE_TOKENS), `[]` when it is anything else. */
export function parseProfileKey(raw: string | null | undefined): string[] {
    if (!raw) return []
    const tokens = raw.split(',')
    if (tokens.length > PROFILE_TOKENS || !tokens.every((t) => TOKEN_PATTERN.test(t))) return []
    return [...new Set(tokens)].sort()
}
